import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type ParsedAuction = {
  startDate: string;
  photoUrls: string[];
  providerDescription: string;
  price: number;
  estimatedRetailPrice?: number; // Optional retail price for discount calculation
  title: string;
  description: string;
  status?: 'active' | 'completed' | 'pending'; // Optional status field from API
  auctionId?: number; // Optional auction ID
  partnerLogo?: string; // Optional partner/provider logo URL
};

interface AuctionContextType {
  activeAuction: ParsedAuction | null;
  setActiveAuction: (auction: ParsedAuction | null) => void;
  clearAuction: () => void;
}

const AuctionContext = createContext<AuctionContextType | undefined>(undefined);

interface AuctionProviderProps {
  children: ReactNode;
}

export const AuctionProvider: React.FC<AuctionProviderProps> = ({ children }) => {
  const [activeAuction, setActiveAuctionState] = useState<ParsedAuction | null>(() => {
    try {
      const savedAuction = localStorage.getItem('auction');
      return savedAuction ? JSON.parse(savedAuction) : null;
    } catch {
      localStorage.removeItem('auction');
      return null;
    }
  });

  useEffect(() => {
    if (activeAuction) {
      localStorage.setItem('auction', JSON.stringify(activeAuction));
    } else {
      localStorage.removeItem('auction');
    }
  }, [activeAuction]);

  const setActiveAuction = (auction: ParsedAuction | null) => {
    setActiveAuctionState(auction);
  };

  const clearAuction = () => {
    setActiveAuctionState(null);
  };

  return (
    <AuctionContext.Provider value={{ activeAuction, setActiveAuction, clearAuction }}>
      {children}
    </AuctionContext.Provider>
  );
};

export const useAuction = () => {
  const context = useContext(AuctionContext);
  if (context === undefined) {
    throw new Error('useAuction must be used within an AuctionProvider');
  }
  return context;
};
